import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { loadFleetConfig, saveFleetConfig } from '../lib/fleetConfigStore';
import { reorderSeasonClasses, addClassesToSeason } from '../lib/clubStore';
import { mergeClassesById } from '../lib/classDivergence';
import { getClubEvents } from '../lib/firebase';
import FleetEditor from '../components/FleetEditor';
import ClassEditor from '../components/ClassEditor';
import StartLocationEditor from '../components/StartLocationEditor';
import ClassDivergencePanel from '../components/ClassDivergencePanel';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import PublishStateNote from '../components/PublishStateNote';

export default function FleetClassManager() {
  const { clubId } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [propagating, setPropagating] = useState(false);

  const reloadEvents = useCallback(async () => {
    const clubEvents = await getClubEvents(clubId);
    setEvents(clubEvents);
    return clubEvents;
  }, [clubId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([loadFleetConfig(clubId), getClubEvents(clubId)])
      .then(([data, clubEvents]) => {
        if (!cancelled) {
          setConfig(data);
          setEvents(clubEvents);
          // Default to latest season
          const sorted = [...clubEvents].sort((a, b) => ((b.date || b.id) > (a.date || a.id) ? 1 : -1));
          setSelectedSeasonId(sorted[0]?.id || '');
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [clubId]);

  const updateConfig = useCallback((updater) => {
    setConfig((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveFleetConfig(clubId, config);
      setDirty(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncClassOrder = async () => {
    if (!selectedSeasonId) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const updated = await reorderSeasonClasses(clubId, selectedSeasonId, config.classes);
      setSyncResult(updated ? `Class order synced to ${selectedSeasonId}.` : `${selectedSeasonId} already in order.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  // Deduplicated classes from all seasons — canonical IDs for matching
  const seasonClasses = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const ev of events) {
      for (const cls of (ev.classes || [])) {
        if (!seen.has(cls.id)) {
          seen.add(cls.id);
          result.push(cls);
        }
      }
    }
    return result;
  }, [events]);

  // Pull classes the schedule references (fleet config) into the season so the
  // app offers them for registration. Writes directly + reloads seasons.
  const handlePropagateToSeason = async (onlyInFleet) => {
    if (!selectedSeasonId || onlyInFleet.length === 0) return;
    setPropagating(true);
    setSyncResult(null);
    setError(null);
    try {
      const added = await addClassesToSeason(clubId, selectedSeasonId, onlyInFleet);
      await reloadEvents();
      setSyncResult(added.length
        ? `Added ${added.length} class${added.length !== 1 ? 'es' : ''} to ${selectedSeasonId}.`
        : `${selectedSeasonId} already has those classes.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setPropagating(false);
    }
  };

  // Pull the season's registration classes into the fleet config so they get a
  // flag/fleet/start (otherwise registered boats match no start). Stages the
  // merge into the editable config; the admin saves + fills in racing details.
  const handlePropagateToFleet = (onlyInSeason) => {
    if (onlyInSeason.length === 0) return;
    setSyncResult(null);
    updateConfig((c) => {
      const { classes } = mergeClassesById(c.classes, onlyInSeason);
      return { ...c, classes };
    });
  };

  if (loading) return <div className="page"><p>Loading fleet config...</p></div>;

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo={`/clubs/${clubId}`} backLabel="Dashboard" title="Fleet &amp; Class Management" />

      <PublishStateNote mode="instant">
        <p>
          Fleets, classes and start locations save immediately — there is no
          publish step here.
        </p>
      </PublishStateNote>

      <div className="notice" style={{ fontSize: '0.85rem' }}>
        <strong>Classes come from your seasons.</strong> The canonical class list lives in{' '}
        <Link to={`/clubs/${clubId}/seasons`}>Seasons</Link> (each class an ID + name). Here you
        attach racing details — flag codes, fleets, start locations and start-sequence order —
        to those same IDs. The <span style={{ color: '#27ae60' }}>&#10003; season</span> badge
        when adding a class means the ID matches one a season already uses, so they stay linked;
        a class added here that no season uses won&rsquo;t be offered for registration. The{' '}
        <strong>Season &harr; class sync</strong> panel below flags any divergence and lets you
        propagate either way.
      </div>

      {error && <div className="error-banner">{error}</div>}

      <FleetEditor
        fleets={config.fleets}
        onChange={(fleets) => updateConfig((c) => ({ ...c, fleets }))}
      />

      <StartLocationEditor
        locations={config.start_locations}
        onChange={(start_locations) => updateConfig((c) => ({ ...c, start_locations }))}
      />

      <ClassEditor
        classes={config.classes}
        fleets={config.fleets}
        startLocations={config.start_locations}
        existingClasses={seasonClasses}
        onChange={(classes) => updateConfig((c) => ({ ...c, classes }))}
      />

      <div className="save-bar">
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
        </button>
        {dirty && <span className="unsaved-indicator">Unsaved changes</span>}
      </div>

      <ClassDivergencePanel
        events={events}
        selectedSeasonId={selectedSeasonId}
        onSelectSeason={(id) => { setSelectedSeasonId(id); setSyncResult(null); }}
        fleetClasses={config.classes}
        onPropagateToSeason={handlePropagateToSeason}
        onPropagateToFleet={handlePropagateToFleet}
        onSyncClassOrder={handleSyncClassOrder}
        propagating={propagating}
        syncing={syncing}
        dirty={dirty}
        syncResult={syncResult}
      />
      </div>
    </>
  );
}
